import { useState } from 'react'

import {
  Text,
  Button,
  GreyGrey0,
  GreyGrey70,
  Primary13Regular,
  Center
} from '@sequencehq/core-components'

import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { ManualTaskLayout } from 'Automations/components'
import { useInvoiceUpdateReviewTask } from '../../hooks/useInvoiceUpdateReviewTask'
import {
  formValuesToConfirmedFields,
  getInvoiceLabel,
  getInvoiceReviewTitle,
  isInvoiceReviewFormValid
} from '../../invoiceUpdateReview.helpers'
import {
  InvoiceReviewFormValues,
  InvoiceUpdateReviewOutcome,
  InvoiceUpdateReviewViewModel
} from '../../types'
import { InvoiceUpdateReviewHeader } from './InvoiceUpdateReviewHeader'
import { InvoiceDocumentPreview } from './InvoiceDocumentPreview'
import { InvoiceFieldsEditorPanel } from './InvoiceFieldsEditorPanel'
import { ModalInvoiceUpdateReviewResponse } from './InvoiceUpdateReviewResponse.modal'

export const InvoiceUpdateReviewContent = ({ taskId }: { taskId: string }) => {
  const { viewModel, isPending, handleClose, completeTask } =
    useInvoiceUpdateReviewTask({ taskId })

  if (isPending) {
    return <ManualTaskLayout.Loading />
  }

  if (!viewModel) {
    return <ManualTaskLayout.Error />
  }

  return (
    <ReviewContent
      taskId={taskId}
      viewModel={viewModel}
      handleClose={handleClose}
      completeTask={completeTask}
    />
  )
}

const ReviewContent = ({
  taskId,
  viewModel,
  handleClose,
  completeTask
}: {
  taskId: string
  viewModel: InvoiceUpdateReviewViewModel
  handleClose: () => void
  completeTask: (outcome: InvoiceUpdateReviewOutcome) => Promise<boolean>
}) => {
  // The working draft the form edits and the preview renders. Seeded once from
  // the agent's proposal; only the four patchable fields are editable.
  const [values, setValues] = useState<InvoiceReviewFormValues>(
    viewModel.proposed
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const isValid = isInvoiceReviewFormValid(values)
  const title = getInvoiceReviewTitle(viewModel.invoice)
  const invoiceLabel = getInvoiceLabel(viewModel.invoice)

  return (
    <ManualTaskLayout
      data-testid={`invoiceUpdateReview.${taskId}`}
      // Actions belong on the top-level layout (not the Header): ManualTaskLayout
      // injects them into the header on desktop and into a bottom bar on mobile.
      // Passing them to Header directly gets overwritten by the layout's inject.
      actions={
        <ManualTaskLayout.Actions>
          <ConfirmAction
            isValid={isValid}
            invoiceLabel={invoiceLabel}
            values={values}
            completeTask={completeTask}
            onInvalid={() => setShowValidationErrors(true)}
          />
        </ManualTaskLayout.Actions>
      }
      header={
        <ManualTaskLayout.Header
          taskId={taskId}
          title={
            <InvoiceUpdateReviewHeader
              customerId={viewModel.invoice.customerId}
              title={`${viewModel.invoice.customerLegalCompanyName}: ${title}`}
            />
          }
          onClose={handleClose}
        />
      }
      content={
        // Live invoice document on the left (flexes to fill), editable form on
        // the right (fixed width) — the `1fr auto` convention shared with the
        // customer-info review.
        <ManualTaskLayout.Content
          // On mobile keep the editable form and drop the invoice document
          // preview, as the other agent reviews do.
          mobilePrimary="review"
          subject={
            // The invoice document preview must never block the review: if it
            // fails to render for an unusual invoice, the operator can still
            // review and apply the field changes, so it's isolated in its own
            // boundary rather than taking the whole modal down.
            <ErrorBoundary
              name="invoiceUpdateReview.documentPreview"
              fallback={<InvoiceDocumentPreviewFallback />}
            >
              <InvoiceDocumentPreview
                invoiceId={viewModel.invoiceId}
                editedValues={values}
                source={viewModel.source}
              />
            </ErrorBoundary>
          }
          review={
            <InvoiceFieldsEditorPanel
              values={values}
              onChange={setValues}
              showValidationErrors={showValidationErrors}
            />
          }
        />
      }
    />
  )
}

const InvoiceDocumentPreviewFallback = () => (
  <Center height="100%" width="100%" backgroundColor={GreyGrey0} padding="24px">
    <Text {...Primary13Regular} color={GreyGrey70}>
      Invoice preview unavailable
    </Text>
  </Center>
)

/**
 * Primary action. When the form is valid it opens the confirmation dialog;
 * otherwise it surfaces the field errors instead of confirming.
 */
const ConfirmAction = ({
  isValid,
  invoiceLabel,
  values,
  completeTask,
  onInvalid
}: {
  isValid: boolean
  invoiceLabel: string
  values: InvoiceReviewFormValues
  completeTask: (outcome: InvoiceUpdateReviewOutcome) => Promise<boolean>
  onInvalid: () => void
}) => {
  if (!isValid) {
    return (
      <Button
        variant="primary"
        onClick={onInvalid}
        data-testid="invoiceUpdateReview.confirm"
      >
        Update invoice
      </Button>
    )
  }

  return (
    <ModalInvoiceUpdateReviewResponse
      trigger={
        <Button variant="primary" data-testid="invoiceUpdateReview.confirm">
          Update invoice
        </Button>
      }
      outcome={{
        outcome: 'CONFIRMED',
        invoice: formValuesToConfirmedFields(values)
      }}
      invoiceLabel={invoiceLabel}
      completeTask={completeTask}
    />
  )
}
